/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { computed, action, set } from '@ember/object';
import { inject as service } from '@ember/service';
import { readOnly } from '@ember/object/computed';
import Component from '@glimmer/component';
import { isBlank } from '@ember/utils';
import { task } from 'ember-concurrency';
import clamp from 'clamp';
import { Periods, Entities } from 'cio/utils/metrics/common';
import EnvironmentService from 'cio/services/environment';
import MetricsService from 'cio/services/metrics';
import CampaignModel from 'cio/models/campaign';
import TagModel from 'cio/models/tag';
import { Metric } from 'cio/utils/metrics/models/metric';
import { pct } from 'cio/helpers/pct';

interface ApiTriggeredBroadcastsTableRowArgs {
  campaign: CampaignModel;
  metricsTask: any;
  onTagClicked: (tag: TagModel) => unknown;
  afterArchiveCampaignAction: (campaign: CampaignModel) => unknown;
  afterDeleteCampaignAction: (campaign: CampaignModel) => unknown;
  afterUnarchiveCampaignAction: (campaign: CampaignModel) => unknown;
}

export default class ApiTriggeredBroadcastsTableRow extends Component<ApiTriggeredBroadcastsTableRowArgs> {
  @service environment!: EnvironmentService;
  @service('metrics') metricsService!: MetricsService;

  // internal API
  @readOnly('campaign.sent') sent!: boolean;

  @computed('args.campaign.type')
  get campaignRouteName() {
    return this.args.campaign.isApiTriggered ? 'broadcasts.broadcast' : 'campaigns.campaign';
  }

  @computed('campaignRouteName', 'args.campaign.type')
  get campaignUndeliverableRouteName() {
    return `${this.campaignRouteName}.overview.${this.args.campaign.isApiTriggered ? 'broadcasts' : 'sent'}`;
  }

  metric!: Metric;
  hasFailed = false;

  @computed('args.campaign.name')
  get displayName() {
    let { name } = this.args.campaign;
    if (isBlank(name)) {
      name = '(Untitled)';
    }
    return name;
  }

  @computed('sent')
  get sentTooltip() {
    let { sent } = this.args.campaign;

    return `${sent} sent in last 24 hours`;
  }

  @computed('campaign.delivered')
  get deliveredTooltip() {
    let { sent, delivered } = this.args.campaign;

    let rate = sent === 0 ? null : delivered / sent;

    return `${delivered} (${pct([rate])}) delivered out of ${sent} sent in the last 24 hours`;
  }

  metricTask = task({ restartable: true }, async () => {
    let metric = await this.metricsService.fetchMetric(
      Entities.CAMPAIGN,
      this.args.campaign.id,
      Periods.DAYS,
      null,
      'email',
      { environmentId: this.environment.current.id, version: 2 },
    );

    set(this, 'metric', metric);
  });

  @action
  didRender(element: HTMLTableRowElement) {
    this.args.metricsTask.perform(this.metricTask);

    let descriptionElem = element.querySelector('.js-campaign-description');
    if (descriptionElem) {
      clamp(descriptionElem, { clamp: 2, useNativeClamp: false });
    }
  }
}
